import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useAccountBlewUpModal } from "../trading-accounts/components/useAccountBlewUpModal";
import { AccountBlewUpModal } from "../trading-accounts/components/AccountBlewUpModal";
import { useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  getTradingSessionById,
  getAllTradesForAnalytics,
  getAccountsByUser,
  updateTradingSession,
  deleteTradingSession,
  uploadSessionImageDirect,
  generateSessionShareLink,
  resolveImageKeys,
  createTrade,
  useQuery,
  useAction,
} from "wasp/client/operations";
import type { TradingSession } from "wasp/entities";
import { Share2, Copy, Check } from "lucide-react";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../client/components/ui/dialog";
import TradeForm, { type TradeFormData, initialTradeFormData } from "../trading-journal/TradeForm";
import { useToast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import useLanguage from "../client/hooks/useLanguage";
import {
  formatSessionDuration,
  extractYouTubeId,
  isTradeInSession,
} from "./sessionUtils";
import { markdownToHtml } from "../notebook/markdownToHtml";
import { compressImage } from "../notebook/imageCompression";
import { trackSessionEnded, trackSessionShared } from '../client/utils/posthog';
import useColorMode, { isDarkBackground } from "../client/hooks/useColorMode";

// État et actions de la page de détail d'une session, sortis de
// SessionDetailPage pour que le mode Lite réutilise EXACTEMENT la même logique
// (sauvegardes, autosave des notes, vidéo, partage, suppression, images) avec
// son propre rendu. Les fenêtres (partage, ajout de trade, compte cramé) sont
// rendues par `dialogs`.

const PARIS_TZ = "Europe/Paris";

function toParisLocalInput(date: Date): string {
  // Format a Date as "YYYY-MM-DDTHH:mm" in Paris time, suitable for datetime-local inputs
  const parts = new Intl.DateTimeFormat("fr-FR", {
    timeZone: PARIS_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  }).formatToParts(date);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

export function parisOffsetLabel(): string {
  const now = new Date();
  const offset = new Intl.DateTimeFormat("fr-FR", { timeZone: PARIS_TZ, timeZoneName: "shortOffset" })
    .formatToParts(now)
    .find((p) => p.type === "timeZoneName")?.value ?? "CET";
  return `Paris (${offset})`;
}

export function useSessionDetail(sessionId: string | undefined) {
  const t = useTranslation();
  const [language] = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: user } = useAuth();

  useEffect(() => {
    if (!user?.id) return;
    const timer = setTimeout(() => {
      localStorage.setItem(`onboarding_sessionDetailVisited_${user.id}`, "true");
    }, 2500);
    return () => clearTimeout(timer);
  }, [user?.id]);

  // Extend the base type with fields added in recent migrations (maxRunup, maxDrawdown, videoUrl)
  // that the Wasp SDK type doesn't include yet until next `wasp build`.
  type SessionWithExtras = TradingSession & {
    maxRunup: number | null;
    maxDrawdown: number | null;
    videoUrl: string | null;
    name: string | null;
  };

  const { data: sessionRaw, isLoading } = useQuery(
    getTradingSessionById,
    { id: sessionId ?? "" },
    { enabled: !!sessionId },
  );
  const session = sessionRaw as SessionWithExtras | undefined;
  const { data: tradesRaw } = useQuery(getAllTradesForAnalytics, { excludeInactive: false });
  const { data: accountsData } = useQuery(getAccountsByUser);

  const updateSessionFn = useAction(updateTradingSession);
  const deleteSessionFn = useAction(deleteTradingSession);
  const uploadImageFn = useAction(uploadSessionImageDirect);
  const resolveImageKeysFn = useAction(resolveImageKeys);
  const generateShareLinkFn = useAction(generateSessionShareLink);

  const [colorMode] = useColorMode();
  const isDark = isDarkBackground(colorMode);

  const [mood, setMood] = useState("");
  const [performance, setPerformance] = useState("");
  const [errorIds, setErrorIds] = useState<string[]>([]);
  const [mentalState, setMentalState] = useState("");

  const [htmlNotes, setHtmlNotes] = useState("");

  const [maxRunup, setMaxRunup] = useState("");
  const [maxDrawdown, setMaxDrawdown] = useState("");
  const [startedAt, setStartedAt] = useState("");
  const [endedAt, setEndedAt] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sessionName, setSessionName] = useState("");
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [shareToken, setShareToken] = useState<string | null>(null);
  const [isShared, setIsShared] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoInput, setVideoInput] = useState("");
  const [videoInputError, setVideoInputError] = useState("");
  const [isEditingVideo, setIsEditingVideo] = useState(false);
  const [isAddTradeOpen, setIsAddTradeOpen] = useState(false);
  const [tradeFormData, setTradeFormData] = useState<TradeFormData>(initialTradeFormData);
  const createTradeFn = useAction(createTrade);
  const { handleViolation, blewUpModalProps } = useAccountBlewUpModal();

  // Refs hold the current HTML so autosave closures always read the latest value
  const htmlNotesRef = useRef("");
  const lastSavedNotesRef = useRef("");
  const initializedRef = useRef(false);
  const notesAutosaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { htmlNotesRef.current = htmlNotes; }, [htmlNotes]);

  // Convert legacy markdown to HTML on load (rétrocompat: old notes stored as markdown)
  function loadContent(raw: string): string {
    if (!raw) return "";
    // If it already looks like HTML, use as-is; otherwise convert from markdown
    return raw.trimStart().startsWith("<") ? raw : markdownToHtml(raw);
  }

  useEffect(() => {
    if (session) {
      setMood(session.mood ?? "");
      setPerformance(session.performance ?? "");
      setErrorIds(((session as any).errors ?? []).map((e: { id: string }) => e.id));
      setMentalState(session.mentalState ?? "");

      const html_notes = loadContent(session.notes ?? "");

      setHtmlNotes(html_notes);
      lastSavedNotesRef.current = html_notes;

      setMaxRunup(session.maxRunup != null ? String(session.maxRunup) : "");
      setMaxDrawdown(session.maxDrawdown != null ? String(session.maxDrawdown) : "");
      setStartedAt(session.startedAt ? toParisLocalInput(new Date(session.startedAt)) : "");
      setEndedAt(session.endedAt ? toParisLocalInput(new Date(session.endedAt)) : "");
      setVideoUrl((session as any).videoUrl ?? "");
      setVideoInput((session as any).videoUrl ?? "");
      setSessionName(session.name ?? "");
      setShareToken((session as any).shareToken ?? null);
      setIsShared((session as any).isShared ?? false);
      initializedRef.current = true;
    }
  }, [session]);

  // Trades whose ENTRY falls within the session window (shared by the chart,
  // the trades list and the PnL totals). See isTradeInSession for the rationale.
  const sessionTrades = useMemo(() => {
    if (!session?.startedAt || !session?.endedAt) return [] as any[];
    return (tradesRaw as any[] ?? []).filter((trade) => isTradeInSession(trade, session));
  }, [session, tradesRaw]);

  const sessionPnl = useMemo(() => {
    if (!session?.startedAt || !session?.endedAt) return null;
    let pnl = 0;
    for (const trade of sessionTrades) {
      pnl += (trade.pnl ?? 0) + (trade.commission ?? 0) + (trade.swaps ?? 0);
    }
    return parseFloat(pnl.toFixed(2));
  }, [session, sessionTrades]);

  const pnlByAccount = useMemo(() => {
    if (!session?.startedAt || !session?.endedAt) return [];
    const accounts = (accountsData as any)?.accounts ?? [];
    const accountNameById = new Map<string, string>();
    for (const a of accounts) accountNameById.set(a.id, a.name);
    const byAccount = new Map<string | null, number>();
    for (const trade of sessionTrades) {
      const net = (trade.pnl ?? 0) + (trade.commission ?? 0) + (trade.swaps ?? 0);
      const key = (trade.accountId ?? null) as string | null;
      byAccount.set(key, (byAccount.get(key) ?? 0) + net);
    }
    return Array.from(byAccount.entries())
      .map(([accountId, pnl]) => ({
        accountId,
        name: accountId ? (accountNameById.get(accountId) ?? "—") : null,
        pnl: parseFloat(pnl.toFixed(2)),
      }))
      .sort((a, b) => b.pnl - a.pnl);
  }, [session, sessionTrades, accountsData]);

  const handleSave = async (
    fields: Partial<{
      mood: string;
      performance: string;
      mentalState: string | undefined;
      opportunities: string | undefined;
      selfEvaluationNotes: string | undefined;
      notes: string | undefined;
      maxRunup: number | null;
      maxDrawdown: number | null;
      startedAt: Date;
      endedAt: Date;
      isActive: boolean;
    }>,
    options?: { silent?: boolean },
  ): Promise<boolean> => {
    if (!session) return false;
    try {
      await updateSessionFn({ id: session.id, ...(fields as any) });
      if (!options?.silent) {
        toast({ title: t.sessionDetail.saved });
      }
      return true;
    } catch {
      if (!options?.silent) {
        toast({ title: t.sessionDetail.saveError, variant: "destructive" });
      }
      return false;
    }
  };

  const handleSaveNotes = useCallback(() => {
    handleSave({ notes: htmlNotesRef.current || undefined });
  }, [session]);

  useEffect(() => {
    if (!session || !initializedRef.current) return;
    if (htmlNotesRef.current === lastSavedNotesRef.current) return;
    if (notesAutosaveTimeoutRef.current) clearTimeout(notesAutosaveTimeoutRef.current);
    notesAutosaveTimeoutRef.current = setTimeout(() => {
      const next = htmlNotesRef.current || "";
      handleSave({ notes: next || undefined }, { silent: true }).then((ok) => {
        if (ok) lastSavedNotesRef.current = next;
      });
    }, 1500);
    return () => {
      if (notesAutosaveTimeoutRef.current) clearTimeout(notesAutosaveTimeoutRef.current);
    };
  }, [htmlNotes, session]);


  const handleDelete = async () => {
    if (!session) return;
    try {
      await deleteSessionFn({ id: session.id });
      toast({ title: t.sessionDetail.deleted });
      navigate("/trading-sessions");
    } catch {
      toast({ title: t.sessionDetail.saveError, variant: "destructive" });
    }
  };

  const handleSaveVideo = async () => {
    const id = extractYouTubeId(videoInput.trim());
    if (!id) {
      setVideoInputError(t.sessionDetail.videoInvalidUrl);
      return;
    }
    setVideoInputError("");
    try {
      await (updateSessionFn as any)({ id: (session as any).id, videoUrl: videoInput.trim() });
      setVideoUrl(videoInput.trim());
      setIsEditingVideo(false);
      toast({ title: t.sessionDetail.videoSaved });
    } catch {
      toast({ title: t.sessionDetail.saveError, variant: "destructive" });
    }
  };

  const handleDeleteVideo = async () => {
    try {
      await (updateSessionFn as any)({ id: (session as any).id, videoUrl: null });
      setVideoUrl("");
      setVideoInput("");
      setIsEditingVideo(false);
      toast({ title: t.sessionDetail.videoDeleted });
    } catch {
      toast({ title: t.sessionDetail.saveError, variant: "destructive" });
    }
  };

  const handleToggleShare = async (enable: boolean) => {
    if (!session) return;
    try {
      const result = await (generateShareLinkFn as any)({ id: session.id, enable });
      setIsShared(result.isShared);
      setShareToken(result.shareToken);
      if (enable) trackSessionShared();
      toast({
        title: enable ? t.sessionDetail.shareEnabled : t.sessionDetail.shareDisabled,
      });
    } catch {
      toast({ title: t.sessionDetail.shareError, variant: "destructive" });
    }
  };

  const handleCopyShareLink = async () => {
    if (!shareToken) return;
    const url = `${window.location.origin}/session/share/${shareToken}`;
    await navigator.clipboard.writeText(url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleSaveName = async (value: string) => {
    if (!session) return;
    if ((value.trim() || null) === (session.name ?? null)) return;
    try {
      await (updateSessionFn as any)({ id: session.id, name: value.trim() || null });
    } catch {
      toast({ title: t.sessionDetail.saveError, variant: "destructive" });
    }
  };

  const handleCreateTrade = async () => {
    if (!tradeFormData.date || !tradeFormData.symbol || !tradeFormData.entryPrice || !tradeFormData.quantity) {
      toast({ title: t.common.error, variant: "destructive" });
      return;
    }
    try {
      const result = await createTradeFn({
        date: new Date(tradeFormData.date),
        symbol: tradeFormData.symbol,
        type: tradeFormData.type,
        status: tradeFormData.status,
        entryPrice: parseFloat(tradeFormData.entryPrice),
        exitPrice: tradeFormData.exitPrice ? parseFloat(tradeFormData.exitPrice) : null,
        quantity: parseFloat(tradeFormData.quantity),
        pnl: tradeFormData.pnl && tradeFormData.status === "closed" ? parseFloat(tradeFormData.pnl) : null,
        commission: parseFloat(tradeFormData.commission) || 0,
        swaps: tradeFormData.swaps ? parseFloat(tradeFormData.swaps) : null,
        notes: tradeFormData.notes || null,
        setupRating: tradeFormData.setupRating,
        tagIds: tradeFormData.tagIds,
        accountId: tradeFormData.accountId || null,
        customFields: tradeFormData.customFields,
      });
      handleViolation((result as any)?.accountViolation);
      toast({ title: t.journal.tradeCreated, description: t.journal.tradeCreatedDescription });
      setIsAddTradeOpen(false);
      setTradeFormData(initialTradeFormData);
    } catch (error: any) {
      toast({ title: t.common.error, description: error.message, variant: "destructive" });
    }
  };

  const handleImageUpload = async (file: File): Promise<{ s3Key: string; downloadUrl: string }> => {
    if (!sessionId) throw new Error("Session ID is required");
    try {
      const compressedFile = await compressImage(file);
      const fileData = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === "string") {
            resolve(reader.result.split(",")[1]);
          } else {
            reject(new Error("Failed to read file"));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(compressedFile);
      });
      const { s3Key, downloadUrl } = await uploadImageFn({
        sessionId,
        fileName: compressedFile.name,
        fileType: compressedFile.type,
        fileData,
      });
      toast({ title: t.notebook.imageUploaded });
      return { s3Key, downloadUrl };
    } catch (error: any) {
      toast({ title: t.notebook.imageUploadError, description: error.message, variant: "destructive" });
      throw error;
    }
  };

  const handleResolveImageKeys = async (s3Keys: string[]): Promise<Record<string, string>> => {
    const { resolved } = await resolveImageKeysFn({ s3Keys });
    return resolved;
  };

  const sessionDate = session?.startedAt
    ? new Date(session.startedAt).toLocaleDateString(language, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

  const actualDurationLabel = formatSessionDuration(session?.actualDuration);
  const plannedDurationLabel = formatSessionDuration(session?.plannedDuration);

  const metricsAnimKey = `session-${session?.id ?? ""}`;

  // Gestionnaires de champs partagés par les deux rendus (Advanced et Lite).
  const handleStartedAtBlur = () => { if (startedAt) handleSave({ startedAt: new Date(startedAt) }); };
  const handleEndedAtBlur = async () => {
    if (endedAt) {
      const ok = await handleSave({ endedAt: new Date(endedAt) });
      if (ok) {
        const startTs = session?.startedAt ? new Date(session.startedAt).getTime() : null;
        const endTs = new Date(endedAt).getTime();
        const durationMinutes = startTs ? Math.round((endTs - startTs) / 60000) : 0;
        trackSessionEnded({
          durationMinutes,
          mood: session?.mood ?? undefined,
          rating: session?.performance ?? undefined,
        });
      }
    }
  };
  const handleMoodChange = (value: string) => {
    setMood(value);
    if (value) handleSave({ mood: value as any });
  };
  const handlePerformanceChange = (value: string) => {
    setPerformance(value);
    if (value) handleSave({ performance: value as any });
  };
  const handleErrorsChange = (next: string[]) => {
    setErrorIds(next);
    handleSave({ errorIds: next } as any);
  };
  const handleMentalStateBlur = () => handleSave({ mentalState: mentalState || undefined });
  const handleMaxRunupBlur = () => handleSave({ maxRunup: maxRunup !== "" ? parseFloat(maxRunup) : null });
  const handleMaxDrawdownBlur = () => handleSave({ maxDrawdown: maxDrawdown !== "" ? parseFloat(maxDrawdown) : null });

  const dialogs = (
    <>
      {/* Dialog: Ajouter un trade */}
      <Dialog open={isAddTradeOpen} onOpenChange={setIsAddTradeOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.sessionDetail.addTrade}</DialogTitle>
            <DialogDescription>{t.journal.newTrade}</DialogDescription>
          </DialogHeader>
          <TradeForm
            formData={tradeFormData}
            setFormData={setTradeFormData}
            onSubmit={handleCreateTrade}
            onCancel={() => setIsAddTradeOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Share dialog */}
      <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              {t.sessionDetail.shareDialogTitle}
            </DialogTitle>
            <DialogDescription>{t.sessionDetail.shareDialogDescription}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">
                {isShared ? t.sessionDetail.shareDisable : t.sessionDetail.shareEnable}
              </span>
              <button
                type="button"
                role="switch"
                aria-checked={isShared}
                onClick={() => handleToggleShare(!isShared)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                  isShared ? "bg-primary" : "bg-input"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                    isShared ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
            </div>

            {isShared && shareToken && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">{t.sessionDetail.shareLinkLabel}</label>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={`${window.location.origin}/session/share/${shareToken}`}
                    className="flex-1 text-xs bg-muted border border-border rounded-md px-3 py-2 font-mono truncate"
                  />
                  <Button size="sm" variant="outline" onClick={handleCopyShareLink} className="shrink-0 gap-1.5">
                    {isCopied ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-green-500" />
                        {t.sessionDetail.shareCopied}
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        {t.sessionDetail.shareCopy}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <AccountBlewUpModal {...blewUpModalProps} />
    </>
  );

  return {
    t, navigate, isDark,
    session, isLoading, tradesRaw, accountsData,
    sessionTrades, sessionPnl, pnlByAccount,
    mood, performance, errorIds, mentalState, setMentalState,
    htmlNotes, setHtmlNotes, handleSaveNotes,
    maxRunup, setMaxRunup, maxDrawdown, setMaxDrawdown,
    startedAt, setStartedAt, endedAt, setEndedAt,
    confirmDelete, setConfirmDelete, handleDelete,
    sessionName, setSessionName, handleSaveName,
    setIsShareDialogOpen, isShared,
    videoUrl, videoInput, setVideoInput, videoInputError, setVideoInputError,
    isEditingVideo, setIsEditingVideo, handleSaveVideo, handleDeleteVideo,
    handleImageUpload, handleResolveImageKeys,
    handleSave,
    handleStartedAtBlur, handleEndedAtBlur,
    handleMoodChange, handlePerformanceChange, handleErrorsChange,
    handleMentalStateBlur, handleMaxRunupBlur, handleMaxDrawdownBlur,
    sessionDate, actualDurationLabel, plannedDurationLabel, metricsAnimKey,
    dialogs,
  };
}
